"use client";

import React from "react";
import { Box, Stack, Typography, useTheme, useMediaQuery } from "@mui/material";
import Image from "next/image";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import InstagramIcon from "@mui/icons-material/Instagram";
import { FeefoSummary } from "@/types/dto/destinations-page/destination-page-types";
import { Reviews } from "../../types/dto/homepage/homepage-types";
import { SectionHeader } from "../common/section-header";
import TestimonialCard from "../common/testimonial-card/testimonial-card";
import FeefoSummaryGrayBar from "../common/feefo-summary-gray-bar/feefo-summary-gray-bar";

interface ReviewsSectionProps {
  reviewsData: Reviews;
  feefoSummary?: FeefoSummary;
}

const ReviewsSection = ({ reviewsData, feefoSummary }: ReviewsSectionProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("lg"));
  return (
    <Box className="fluid-box" sx={{ background: "var(--theme-palette-background-cool_strong)" }}>
      <Box
        sx={{
          padding: {
            xs: "4rem 2.4rem", // <600px
            md: "4rem 10rem", // >=600px and <1440px
            xl: "4rem 0" // >=1440px
          },
          width: "100%"
        }}
        className="full-width-container
      0"
        data-track="visibility"
        data-module-name="Customer Testimonial Section"
        data-ranking-algorithm="manual"
        data-module-ranking-position="9"
        data-testid="homepage-reviews-section-root"
        component="section"
      >
        <SectionHeader
          headerLevel="h2"
          headerText={reviewsData.title}
          headerSx={{ textAlign: "center", textTransform: "none !important" }}
          sx={{ display: "flex", flexDirection: "row", justifyContent: "center" }}
          headerTestId="homepage-reviews-section-header"
        />
        
        <FeefoSummaryGrayBar 
          feefoSummary={feefoSummary} 
          testId="homepage-reviews-section-summary" 
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: "20px",
            minHeight: { sm: 400, md: 500 }
          }}
          data-testid="homepage-reviews-section-grid"
        >
          {/* Left column: full height box (first testimonial) */}
          {reviewsData?.testimonialCards[0] && (
            <Box
              sx={{
                flex: 1,
                backgroundColor: "background.default",
                borderRadius: "20px",
                maxHeight: "684px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "24px"
              }}
              data-testid="homepage-reviews-section-featured-card"
            >
              {reviewsData.testimonialCards[0].image?.imageUrl && (
                <Box sx={{ width: "100%", height: "386px", position: "relative" }}>
                  <Image
                    src={reviewsData.testimonialCards[0].image.imageUrl}
                    alt={reviewsData.testimonialCards[0].image.altText || ""}
                    fill
                    style={{ borderRadius: "20px", objectFit: "cover", objectPosition: "center" }}
                    quality={100}
                    priority
                  />
                </Box>
              )}
              <Stack sx={{ mt: "57px" }}>
                <Typography
                  color="text.primary"
                  variant="subtitle_lg"
                  component="p"
                  fontWeight={500}
                  lineHeight="150%"
                >
                  {reviewsData.testimonialCards[0].copy}
                </Typography>
                <Stack
                  display="flex"
                  direction="row"
                  gap="8px"
                  alignItems="center"
                  marginTop="57px"
                >
                  {reviewsData.testimonialCards[0].image?.imageUrl && (
                    <InstagramIcon sx={{ width: "24px", height: "24px" }} />
                  )}
                  <Typography variant="subtitle_lg" component="p" color="text.primary" fontWeight={700}>
                    {reviewsData.testimonialCards[0].reviewerNameSocialMediaHandle}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Right columns: next four testimonials, two per column */}
          {[1, 2]
            .filter((colIdx) => {
              if (isMobileOrTablet) {
                return colIdx === 1;
              }
              return true;
            })
            .map((colIdx) => (
              <Box
                key={colIdx}
                sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}
                data-testid={`homepage-reviews-section-column-${colIdx}`}
              >
                {[0, 1].map((rowIdx) => {
                  const idx = colIdx * 2 - 1 + rowIdx;
                  const card = reviewsData?.testimonialCards[idx];
                  if (!card) return null;
                  return (
                    <TestimonialCard
                      key={idx}
                      title={card.title}
                      copy={card.copy}
                      reviewerNameSocialMediaHandle={card.reviewerNameSocialMediaHandle}
                      starRating={card.starRating}
                      ctaUrl={card?.cta?.cta?.url || undefined}
                      ctaTitle={card?.cta?.cta?.title || undefined}
                      testId={`homepage-reviews-section-card-${idx}`}
                    />
                  );
                })}
              </Box>
            ))}
        </Box>

        <Stack alignItems="center" mt="26px">
          <ColletteButton
            variant="outlined"
            size="large"
            href={reviewsData?.cta?.cta?.url || "#"}
            label={reviewsData?.cta?.cta?.title || "Read All Reviews"}
            testId="homepage-hero-review-cta-button"
            data-track="click"
            sx={{
              backgroundColor: "background.default"
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default ReviewsSection;
